import React, { useRef, useState, useEffect } from 'react';
import { Box, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Link, Paper } from '@mui/material';
import { indigo } from '@mui/material/colors';
import get from 'lodash/get';
import { orderBy as lodashOrderBy } from 'lodash';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { generatePath, useHistory } from 'react-router-dom';
import TableToCSVGenerator from './TableToCSVGenerator';
import { formatNumber, formatValueOfMeals, csvFileNameGenerators } from '../helpers/formatters';
import { Colors } from '../assets/theme/Colors';
import { formatDateToMonthName, formatDateToWeekName } from '../helpers/dates';
import { getUserFullName } from '../helpers/user';
import routes from '../routes';

const paperSx = {
  padding: { sx: 1, sm: 2 },
  '& table': {
    '& thead': {
      '& th': {
        fontWeight: 600,
        padding: '3px 12px',
        whiteSpace: 'nowrap',
      },
    },
    '& tbody': {
      '& td': {
        padding: '3px 12px',
        borderBottom: 'none',
      },
      '& td:first-child': {
        minWidth: '200px',
      },
    },
  },
};
const rowTotalSx = {
  backgroundColor: 'primary.main',
  color: '#fff',
};
const footerTotalColumnSx = {
  left: 0,
  position: 'sticky',
};
const oddColumnSx = {
  background: `${indigo[50]} !important`,
};
const diffOddSx = {
  color: `${Colors.tableDiff.odd.contrastText} !important`,
  boxShadow: `inset 0px 0px 1px 100px ${Colors.tableDiff.odd.color}`,
};
const diffEvenSx = {
  color: `${Colors.tableDiff.even.contrastText} !important`,
  boxShadow: `inset 0px 0px 1px 100px ${Colors.tableDiff.even.color}`,
};
const periodStickyHeaderSx = {
  top: 0,
  position: 'sticky',
  zIndex: 1,
  background: '#fff',
  borderBottom: 0,
};
const entityTypeStickyHeaderSx = {
  top: 36,
  position: 'sticky',
  zIndex: 1,
  background: '#fff',
  boxShadow: 'inset 0 -2px 0px 0px #7986cb',
};
const siteColumnHeadSx = {
  position: 'sticky',
  top: 0,
  left: 0,
  background: '#fff',
  boxShadow: 'inset 0 -2px 0px 0px #7986cb',
  zIndex: 2,
};
const siteColumnSx = {
  maxWidth: '200px',
  width: '200px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  position: 'sticky',
  left: 0,
  background: '#fff',
};
const siteDirectorColumnHeadSx = {
  position: 'sticky',
  top: 0,
  left: 200,
  background: '#fff',
  boxShadow: 'inset 0 -2px 0px 0px #7986cb',
  zIndex: 2,
};
const siteDirectorColumnSx = {
  maxWidth: '200px',
  width: '200px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  position: 'sticky',
  left: 200,
  background: '#fff',
};
const rescueSizeColumnHeadSx = {
  position: 'sticky',
  top: 0,
  left: 373,
  background: '#fff',
  boxShadow: '2px 0 0px 0px #7986cb, inset 0 -2px 0px 0px #7986cb',
  zIndex: 2,
  maxWidth: '280px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
};
const rescueSizeColumnSx = {
  whiteSpace: 'nowrap',
  position: 'sticky',
  left: 373,
  background: '#fff',
  maxWidth: '280px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  boxShadow: '2px 0 0px 0px #7986cb',
};
const rescueSizeColumnForReceivingAdminSx = {
  whiteSpace: 'nowrap',
  position: 'sticky',
  left: 0,
  background: '#fff',
  maxWidth: 200,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  boxShadow: '2px 0 0px 0px #7986cb',
}
const sortByCellSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  cursor: 'pointer',
};
const justifyContentCenterSx = {
  justifyContent: 'center',
};
const descriptionsHeaderSx = {
  borderBottom: 'none',
};
const tableTotalRowSx = {
  fontWeight: 600,
};
const tableContainerSx = {
  height: 'calc(100vh - 450px)',
  overflow: 'scroll',
  '&&::-webkit-scrollbar': {
    WebkitAppearance: 'none',
    width: '0.65rem',
    height: '0.65rem',
  },
  '&&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, .5)',
    WebkitBoxShadow: '0 0 1px rgba(255, 255, 255, .5)',
  },
  '@media (max-height: 680px)': {
    height: '50vh',
  },
};
const borderLeftColumnSx = {
  borderLeft: `2px solid ${indigo[300]}`,
};

const ReceiverReportTable = ({ filters, rescueReportData, showSiteDataColumns }) => {
  const tableRef = useRef();
  const history = useHistory();
  const granularity = get(filters, 'granularity');
  const [rescueReport, setRescueReport] = useState(rescueReportData);

  // Generate dynamic filename based on date range
  const generateCsvFileName = () => {
    return csvFileNameGenerators.receivingAgencyReport({
      dateRange: {
        startDate: filters?.startDate,
        endDate: filters?.endDate
      }
    });
  };

  useEffect(() => {
    setRescueReport(rescueReportData);
  }, [rescueReportData]);

  const [orderBy, setOrderBy] = useState({
    period: null,
    entityType: 'site',
    measure: null,
    order: 'asc',
  });

  const handleSortBy = (period, entityType, measure) => {
    const foundOrderBy =
      orderBy.period === period && orderBy.entityType === entityType && orderBy.measure === measure
        ? orderBy.order
        : null;
    const order = foundOrderBy ? (foundOrderBy === 'asc' ? 'desc' : 'asc') : 'asc';

    const saveOrders = () =>
      setOrderBy({
        period: period,
        entityType: entityType,
        measure: measure,
        order: order,
      });

    if (['site', 'siteDirector'].indexOf(entityType) >= 0) {
      setRescueReport({
        ...rescueReport,
        sites: lodashOrderBy(
          rescueReport.sites,
          row => {
            if (entityType === 'site') {
              return row.site.name;
            }

            if (entityType === 'siteDirector') {
              return getUserFullName(row.site.site_director);
            }
          },
          order
        ),
      });

      return saveOrders();
    }

    setRescueReport({
      ...rescueReport,
      sites: rescueReport.sites.map((siteRow, siteIndex) => {
        if (measure !== null) {
          if (period !== null) {
            return {
              ...siteRow,
              receiving_agencies: lodashOrderBy(
                siteRow.receiving_agencies,
                rescueSizeRow => {
                  const value = rescueSizeRow[entityType].periods[period][measure];
                  return value !== null ? value : -1;
                },
                order
              ),
            };
          }

          // period is not set, we want to sort by row total
          const value = siteIndex[entityType].total[measure];

          return value !== null ? value : -1;
        }

        if (entityType === 'receivingAgency') {
          return {
            ...siteRow,
            receiving_agencies: lodashOrderBy(
              siteRow.receiving_agencies,
              rescueSizeRow => rescueSizeRow.receiving_agency.name,
              order
            ),
          };
        }

        return siteRow;
      }),
    });

    return saveOrders();
  };

  const renderSortBy = (index, entityType, label, measure) => (
    <Box
      onClick={() => handleSortBy(index, entityType, measure)}
      sx={[
        sortByCellSx,
        entityType !== 'site' &&
          entityType !== 'siteDirector' &&
          entityType !== 'receivingAgency' &&
          justifyContentCenterSx,
      ]}
    >
      <>
        <span>{label !== null && label}</span>

        {orderBy.period === index && orderBy.entityType === entityType && (
          <>{orderBy.order === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}</>
        )}
      </>
    </Box>
  );

  const renderCount = (count, format = 'number') => {
    if (format === 'value') {
      return formatValueOfMeals(count);
    }

    return formatNumber(count);
  };

  const renderEntityColumn = (index, entityName, entityType, showBorder = false, isOdd = null) => (
    <TableCell
      variant="head"
      align="center"
      sx={[
        entityTypeStickyHeaderSx,
        descriptionsHeaderSx,
        showBorder && borderLeftColumnSx,
        isOdd === true && diffOddSx,
        isOdd === false && diffEvenSx,
      ]}
    >
      {renderSortBy(index, entityType, entityName, 'count')}
    </TableCell>
  );

  const renderRescueSizesRow = (siteIndex, siteRow) => {
    const rescueSizesList = siteRow.receiving_agencies || [];

    if (rescueSizesList.length === 0) {
      return null;
    }

    const _renderRowEntity = (index, periodIndex, data, format = 'number', showBorder = false, isOdd = null) => (
      <TableCell
        variant="head"
        align="center"
        sx={[
          index % 2 && oddColumnSx,
          isOdd === true && diffOddSx,
          isOdd === false && diffEvenSx,
          showBorder && borderLeftColumnSx,
        ]}
        data-period={periodIndex}
      >
        {renderCount(data.count, format)}
      </TableCell>
    );

    return rescueSizesList.map((rescueSize, rescueSizeIndex) => (
      <React.Fragment key={`${siteIndex}-${rescueSizeIndex}`}>
        <TableRow>
          {showSiteDataColumns && rescueSizeIndex === 0 && (
            <>
              <TableCell
                rowSpan={siteRow.receiving_agencies.length}
                sx={[siteColumnSx, siteIndex % 2 && oddColumnSx]}
              >
                {siteRow.site.name}
              </TableCell>
              <TableCell
                rowSpan={siteRow.receiving_agencies.length}
                sx={[siteDirectorColumnSx, siteIndex % 2 && oddColumnSx]}
              >
                {siteRow.site.site_director && (
                  <Link
                    onClick={() =>
                      history.push(generatePath(routes.userEditNew, { id: siteRow.site.site_director.id }))
                    }
                    style={{ cursor: 'pointer' }}
                    underline="hover"
                  >
                    {getUserFullName(siteRow.site.site_director)}
                  </Link>
                )}

                {!siteRow.site.site_director && 'n/a'}
              </TableCell>
            </>
          )}

          <TableCell
            sx={[
              showSiteDataColumns && rescueSizeColumnSx,
              !showSiteDataColumns && rescueSizeColumnForReceivingAdminSx,
              siteIndex % 2 && oddColumnSx,
            ]}
          >
            {rescueSize.receiving_agency.name}
          </TableCell>

          {rescueReport.periods.map((period, periodIndex) => (
            <React.Fragment key={periodIndex}>
              {_renderRowEntity(siteIndex, periodIndex, rescueSize.rescues.periods[periodIndex], 'number', true)}
              {_renderRowEntity(siteIndex, periodIndex, rescueSize.meals.periods[periodIndex])}
              {_renderRowEntity(siteIndex, periodIndex, rescueSize.lbs.periods[periodIndex])}
              {showSiteDataColumns && _renderRowEntity(siteIndex, periodIndex, rescueSize.value.periods[periodIndex], 'value')}
            </React.Fragment>
          ))}

        </TableRow>
      </React.Fragment>
    ));
  };

  const renderTotalRow = totalData => {
    const _renderTotalRowEntityPeriod = (index, sitetotalDataEntityTotalPeriod, showBorder = false) => (
      <>
        <TableCell
          variant="head"
          align="center"
          sx={[rowTotalSx, tableTotalRowSx, showBorder && borderLeftColumnSx ]}
        >
          {renderCount(sitetotalDataEntityTotalPeriod.count)}
        </TableCell>
      </>
    );

    return (
      <TableRow>
        <TableCell
          colSpan={showSiteDataColumns ? 3 : 0}
          variant="head"
          sx={[footerTotalColumnSx, rowTotalSx, tableTotalRowSx]}
        >
          Total
        </TableCell>

        {rescueReport.periods.map((row, index) => (
          <React.Fragment key={index}>
            {_renderTotalRowEntityPeriod(index, totalData.rescues.periods[index], true)}

            {_renderTotalRowEntityPeriod(index, totalData.meals.periods[index])}

            {_renderTotalRowEntityPeriod(index, totalData.lbs.periods[index])}

            {showSiteDataColumns && _renderTotalRowEntityPeriod(index, totalData.value.periods[index])}
          </React.Fragment>
        ))}
      </TableRow>
    );
  };

  if (!rescueReportData.sites.length) {
    return (
      <Box my={2}>
        <Paper sx={paperSx}>Please use &quot;RUN&quot; button to generate the report.</Paper>
      </Box>
    );
  }

  return (
    <Box my={2}>
      <Paper sx={paperSx}>
        <TableToCSVGenerator tableRef={tableRef} fileName={generateCsvFileName()} />
        <TableContainer sx={tableContainerSx} className="table-container">
          <Table aria-label="spanning table" size="small"  ref={tableRef} id="rescue-report-table">
            <TableHead>
              <TableRow>
                {showSiteDataColumns && (
                  <TableCell
                    rowSpan={2}
                    sx={[descriptionsHeaderSx, siteColumnHeadSx]}
                  >
                    {renderSortBy(null, 'site', 'Site', null)}
                  </TableCell>
                )}
                {showSiteDataColumns && (
                  <TableCell
                    rowSpan={2}
                    sx={[descriptionsHeaderSx, siteDirectorColumnHeadSx]}
                  >
                    {renderSortBy(null, 'siteDirector', 'Site Director', null)}
                  </TableCell>
                )}
                <TableCell
                  rowSpan={2}
                  sx={[
                    descriptionsHeaderSx,
                    showSiteDataColumns && rescueSizeColumnHeadSx,
                    !showSiteDataColumns && siteColumnHeadSx,
                  ]}
                >
                  {renderSortBy(null, 'receivingAgency', 'Receiving Agency', null)}
                </TableCell>

                {rescueReport.periods.map((row, index) => (
                  <React.Fragment key={index}>
                    <TableCell
                      colSpan={showSiteDataColumns ? 4 : 3}
                      variant="head"
                      align="center"
                      sx={[periodStickyHeaderSx, descriptionsHeaderSx, borderLeftColumnSx]}
                    >
                      {granularity === 'monthly' ? formatDateToMonthName(row) : formatDateToWeekName(row)}
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>

              <TableRow>
                {rescueReport.periods.map((row, index) => (
                  <React.Fragment key={index}>
                    {renderEntityColumn(index, '# Rescues', 'rescues',true)}

                    {renderEntityColumn(index, '# Meals', 'meals')}

                    {renderEntityColumn(index, '# Lbs', 'lbs')}

                    {showSiteDataColumns && renderEntityColumn(index, '$', 'value')}
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rescueReport.sites.map((siteRow, siteIndex) => (
                  <React.Fragment key={siteIndex}>{renderRescueSizesRow(siteIndex, siteRow)}</React.Fragment>
              ))}

              {renderTotalRow(rescueReport.total)}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ReceiverReportTable;
